/**
 * Job Comments Hooks
 * React Query hooks for fetching and managing job comments
 *
 * Following DDD Principles:
 * - Hooks consume job-comments service
 * - React Query handles caching and state management
 * - Components receive DTOs ready for display
 * - NO business logic in hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jobCommentsService } from '@/services/job-comments.service';
import type { CommentDto, AddCommentRequest, EditCommentRequest } from '../types/comment';

/**
 * Job Comment query key factory
 * Provides consistent cache keys for React Query
 */
export const jobCommentKeys = {
  all: ['job-comments'] as const,
  lists: () => [...jobCommentKeys.all, 'list'] as const,
  list: (jobId: string) => [...jobCommentKeys.lists(), jobId] as const,
};

/**
 * Hook to fetch job comments
 * @param jobId - The job ID
 */
export function useJobComments(jobId: string) {
  return useQuery<CommentDto[], Error>({
    queryKey: jobCommentKeys.list(jobId),
    queryFn: () => jobCommentsService.getJobComments(jobId),
    staleTime: 30 * 1000, // 30 seconds - comments update frequently
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled: !!jobId,
  });
}

/**
 * Hook to add a new comment
 * Implements optimistic updates for better UX
 * @param jobId - The job ID
 */
export function useAddComment(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AddCommentRequest) =>
      jobCommentsService.addComment(jobId, request),

    // Optimistic update: Add comment to cache immediately
    onMutate: async (newComment) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: jobCommentKeys.list(jobId) });

      // Snapshot previous state for rollback
      const previousComments = queryClient.getQueryData<CommentDto[]>(jobCommentKeys.list(jobId));

      // Optimistically add temporary comment to cache
      queryClient.setQueryData<CommentDto[]>(jobCommentKeys.list(jobId), (old = []) => [
        ...old,
        {
          id: `temp-${Date.now()}`,
          jobId,
          userId: 'current-user', // Will be replaced by server
          content: newComment.content,
          mentions: [],
          parentCommentId: newComment.parentCommentId || null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } as CommentDto,
      ]);

      return { previousComments };
    },

    // On error: Rollback to previous state
    onError: (_err, _newComment, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(jobCommentKeys.list(jobId), context.previousComments);
      }
    },

    // Always refetch to get server data with real IDs and timestamps
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: jobCommentKeys.list(jobId) });
    },
  });
}

/**
 * Hook to edit an existing comment
 * Updates cache optimistically
 */
export function useEditComment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ commentId, request }: { commentId: string; request: EditCommentRequest }) =>
      jobCommentsService.editComment(commentId, request),

    onSuccess: (updatedComment) => {
      // Update cache for this job
      queryClient.setQueryData<CommentDto[]>(
        jobCommentKeys.list(updatedComment.jobId),
        (old = []) => old.map(c => c.id === updatedComment.id ? updatedComment : c)
      );
    },
  });
}

/**
 * Hook to delete a comment
 * REMOVED: Comments cannot be deleted (audit trail requirement)
 * This hook is kept for backwards compatibility but throws an error
 */
export function useDeleteComment() {
  return useMutation({
    mutationFn: () => {
      throw new Error('Comment deletion is not allowed for audit trail compliance');
    },
  });
}
